import React, { useMemo, memo, FC, useEffect, useRef, useState } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';
import DOMPurify from 'isomorphic-dompurify';
import Skeleton from 'react-loading-skeleton';
import * as Styled from './OCountdownEyebrowBanner.styles';
import { useCountdownBannerTarget } from '../../hooks/useCountdownBannerTarget';
import { useDCAPageModelStore } from '@marriott/mi-store-utils';
import { normalizeAEMBoolean } from '../../modules/utils/src/helper';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ABody, ABodySize, ABodyColor, MButton, MButtonType, MButtonSize, MTimer } from '@marriott/mi-web-ui-kit';

import {
  OCountdownEyebrowBannerAEMProps,
  OCountdownEyebrowBannerPresentationProps,
  OCountdownEyebrowBannerTimer,
  OCountdownEyebrowBannerABody,
  OCountdownEyebrowBannerButton,
} from './OCountdownEyebrowBanner.types';

type DCAResponseState = Record<string, unknown> & {
  isTargetContentLoaded?: boolean;
  isTargetContentError?: boolean;
};

type DcaSyncStatus = 'success' | 'error' | 'pending';

interface DCAMboxData {
  mboxName: string;
  dcaResponse?: DCAResponseState | null;
  uxlResponse?: DCAResponseState | null;
  [key: string]: unknown;
}

interface DCAPageModel {
  mboxData?: DCAMboxData[];
  [key: string]: unknown;
}

const COMPONENT_NAME = 'o-cobrand-OCountdownEyebrowBanner';
const TEST_ID = 'cobrand-OCountdownEyebrowBanner';
export const OCountdownEyebrowBannerConfig = {
  emptyLabel: 'OCountdownEyebrowBanner',
  isEmpty: () => false,
  resourceType: 'mi-aem-utils/components/content/cobrand/o-countdown-eyebrow-banner',
};

const getAccessibleTimerAnnouncement = (targetDate?: string): string => {
  if (!targetDate) {
    return '';
  }

  const target = new Date(targetDate).getTime();

  if (Number.isNaN(target)) {
    return '';
  }

  const difference = target - Date.now();

  if (difference <= 0) {
    return '0 days, 0 hours, 0 minutes remaining';
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

  return `${days} days, ${hours} hours, ${minutes} minutes remaining`;
};

const getAccessiblePromoText = (html?: string): string => {
  if (!html) {
    return '';
  }

  const cleanEl = DOMPurify.sanitize(html, {
    RETURN_DOM: true,
  }) as unknown as HTMLElement;

  const deletedElements = cleanEl.querySelectorAll('del, strike, s');
  deletedElements.forEach(element => element.remove());

  return (cleanEl.textContent || '').trim().replace(/\s+/g, ' ');
};

/**
 * Presentation component for OCountdownEyebrowBanner
 * Contains the UI logic without AEM-specific concerns
 */
export const OCountdownEyebrowBannerComp: FC<OCountdownEyebrowBannerPresentationProps> = memo(props => {
  const {
    aBody,
    mTimer,
    mButton,
    trackingProperties,
    authorMode,
    expirationDate,
    isLoading = false,
    onCountdownComplete,
  } = props;

  // Extract labels from mTimer
  const daysLabel = mTimer?.aBody?.bodyText || 'DAYS';
  const hoursLabel = mTimer?.aBody2?.bodyText || 'HOURS';
  const minutesLabel = mTimer?.aBody3?.bodyText || 'MINUTES';
  const secondsLabel = mTimer?.aBody4?.bodyText || 'SECONDS';
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastPoliteAnnouncementRef = useRef('');
  const hasAnnouncedInitialTimerRef = useRef(false);
  const [initialTimerAnnouncement, setInitialTimerAnnouncement] = useState('');
  const promoBodyText = aBody?.bodyText;
  const promoHasOmittedTags = /<(del|strike|s)\b/i.test(promoBodyText ?? '');
  const accessiblePromoText = useMemo(
    () => (promoHasOmittedTags ? getAccessiblePromoText(promoBodyText) : ''),
    [promoBodyText, promoHasOmittedTags]
  );

  const hideTimerStaticSummary = () => {
    const timerRoot = wrapperRef.current?.querySelector<HTMLElement>(
      '[data-testid="web-ui-kit-MTimer"], [data-testid="mtimer-component"]'
    );

    if (!timerRoot) {
      return;
    }

    const staticSummaryNode = Array.from(timerRoot.querySelectorAll<HTMLElement>('*')).find(
      node => node.getAttribute('aria-live') !== 'polite' && node.textContent?.includes('remaining')
    );

    if (staticSummaryNode) {
      staticSummaryNode.setAttribute('aria-hidden', 'true');
    }
  };

  useEffect(() => {
    setInitialTimerAnnouncement('');
    hasAnnouncedInitialTimerRef.current = false;
  }, [authorMode, expirationDate, isLoading]);

  useEffect(() => {
    if (authorMode || isLoading || hasAnnouncedInitialTimerRef.current) {
      return;
    }

    const nextAnnouncement = getAccessibleTimerAnnouncement(expirationDate);

    if (!nextAnnouncement) {
      return;
    }

    setInitialTimerAnnouncement(nextAnnouncement);
    hasAnnouncedInitialTimerRef.current = true;
  }, [authorMode, expirationDate, isLoading]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    hideTimerStaticSummary();
  }, [initialTimerAnnouncement, isLoading]);

  useEffect(() => {
    if (!wrapperRef.current || isLoading) {
      return;
    }

    const observer = new MutationObserver(() => {
      hideTimerStaticSummary();
      const politeNode = wrapperRef.current?.querySelector<HTMLElement>(
        '[data-testid="web-ui-kit-MTimer"] [aria-live="polite"], [data-testid="mtimer-component"] [aria-live="polite"]'
      );
      const politeText = politeNode?.textContent?.trim().replace(/\s+/g, ' ') || '';

      if (!politeText || politeText === lastPoliteAnnouncementRef.current) {
        return;
      }

      lastPoliteAnnouncementRef.current = politeText;
      setInitialTimerAnnouncement('');
    });

    observer.observe(wrapperRef.current, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [isLoading]);

  return (
    <Styled.Wrapper
      ref={wrapperRef}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      data-author-mode={authorMode ? 'true' : 'false'}
      data-tracking-properties={trackingProperties ? JSON.stringify(trackingProperties) : undefined}
    >
      <div className="container py-2">
        <div className="row align-items-center">
          <div
            className={clsx(
              'col-12',
              'col-lg-4',
              'd-flex',
              'justify-content-center',
              'justify-content-lg-start',
              'mb-3',
              'mb-lg-0'
            )}
          >
            <MTimer
              targetDate={expirationDate}
              daysLabel={daysLabel}
              hoursLabel={hoursLabel}
              minutesLabel={minutesLabel}
              secondsLabel={secondsLabel}
              isLoading={isLoading}
              isAuthorMode={authorMode}
              onComplete={onCountdownComplete}
            />
            {initialTimerAnnouncement ? (
              <span
                className="sr-only"
                data-testid="countdown-banner-live-region"
                aria-live="polite"
                aria-atomic="true"
              >
                {initialTimerAnnouncement}
              </span>
            ) : null}
          </div>

          <div className="col-12 col-lg-6 text-center mb-3 mb-lg-0">
            {isLoading ? (
              <Styled.SkeletonWrapper>
                <Styled.SkeletonText>
                  <Skeleton
                    height={32}
                    baseColor="var(--t-neutral-color-10, #ebebeb)"
                    highlightColor="var(--t-neutral-color-05, #f5f5f5)"
                  />
                </Styled.SkeletonText>
              </Styled.SkeletonWrapper>
            ) : (
              aBody?.bodyText && (
                <>
                  <ABody
                    size={aBody.size ?? ABodySize.S}
                    color={aBody.color ?? ABodyColor.Default}
                    bodyText={aBody.bodyText}
                    className="pb-0"
                    aria-hidden={promoHasOmittedTags ? true : undefined}
                  />
                  {promoHasOmittedTags && accessiblePromoText ? (
                    <span className="sr-only" data-testid="countdown-banner-accessible-promo">
                      {accessiblePromoText}
                    </span>
                  ) : null}
                </>
              )
            )}
          </div>

          <div className="col-12 col-lg-2 d-flex justify-content-center justify-content-lg-end">
            {isLoading ? (
              <Styled.SkeletonWrapper>
                <Styled.SkeletonButton>
                  <Skeleton
                    height={32}
                    baseColor="var(--t-neutral-color-10, #ebebeb)"
                    highlightColor="var(--t-neutral-color-05, #f5f5f5)"
                  />
                </Styled.SkeletonButton>
              </Styled.SkeletonWrapper>
            ) : (
              mButton?.buttonText &&
              mButton.ctaLink && (
                <MButton
                  buttonText={mButton.buttonText}
                  variant={mButton.type ?? MButtonType.Tertiary}
                  size={mButton.size ?? MButtonSize.M}
                  href={mButton.ctaLink}
                  isExternal={mButton.openInANewTab === 'true'}
                  trackingProperties={trackingProperties}
                  data-faic-href={trackingProperties?.['faicUrl']}
                  data-faic-mcd={trackingProperties?.['faicMCD']}
                />
              )
            )}
          </div>
        </div>
      </div>
    </Styled.Wrapper>
  );
});

OCountdownEyebrowBannerComp.displayName = 'OCountdownEyebrowBannerComp';

/**
 * Helper function to check if a countdown timer is expired (all zeros)
 */
const isTimerExpired = (expirationDate: string | undefined): boolean => {
  if (!expirationDate) return true;

  try {
    const now = new Date().getTime();
    const target = new Date(expirationDate).getTime();
    return target - now <= 0;
  } catch {
    return true;
  }
};

const getDcaSyncStatus = (
  syncWithDcaMbox: string | undefined,
  isAuthorMode: boolean | undefined,
  dcaPageModel: DCAPageModel | null | undefined
): DcaSyncStatus => {
  if (!syncWithDcaMbox || isAuthorMode) {
    return 'success';
  }

  const dcaMboxData = dcaPageModel?.mboxData?.find(item => item.mboxName === syncWithDcaMbox);

  if (!dcaMboxData) {
    return 'pending';
  }

  const responseStates = [dcaMboxData.dcaResponse, dcaMboxData.uxlResponse].filter(
    (response): response is DCAResponseState => !!response && typeof response === 'object'
  );

  if (responseStates.some(response => response.isTargetContentError === true)) {
    return 'error';
  }

  if (!responseStates.length) {
    return dcaMboxData.dcaResponse === null || dcaMboxData.uxlResponse === null ? 'error' : 'pending';
  }

  if (responseStates.some(response => response.isTargetContentLoaded === false)) {
    return 'pending';
  }

  return 'success';
};

export const OCountdownEyebrowBanner = (props: OCountdownEyebrowBannerAEMProps) => {
  const model = props.model || props || {};
  const { styleclass, componentId, targetMbox, displayWithoutTarget, syncWithDcaMbox, ...rest } = model as Record<
    string,
    unknown
  > & {
    styleclass?: string;
    componentId?: string;
    targetMbox?: string;
    displayWithoutTarget?: boolean | string;
    syncWithDcaMbox?: string;
  };

  // Check author mode using isInEditor (AEM standard) OR isAuthorMode (URL-based fallback)
  const isAuthorMode = props.isInEditor || props.isAuthorMode;

  // Fetch from Adobe Target if mbox is configured and NOT in author mode
  // Skip Target calls in author mode to avoid external API calls during authoring
  const { data: targetData, isLoading } = useCountdownBannerTarget(isAuthorMode ? undefined : targetMbox);

  // Get DCA page model to check if synced component has data
  const dcaPageModel = useDCAPageModelStore(state => state.pageModel as DCAPageModel);

  // Handle displayWithoutTarget using normalized boolean helper
  const shouldDisplayWithoutTarget = normalizeAEMBoolean(displayWithoutTarget);

  const authoredExpirationDate = rest['expirationDate'] as string | undefined;

  // Determine which expiration date to use for hiding logic
  // If Target data is available, use Target's date; otherwise use AEM's date
  const effectiveExpirationDate = targetData ? targetData.expirationDate : authoredExpirationDate;

  // Check if the EFFECTIVE timer is expired (the one we're actually using)
  // MUST be called before any conditional returns to satisfy Rules of Hooks
  const effectiveTimerExpired = useMemo(() => {
    return isTimerExpired(effectiveExpirationDate);
  }, [effectiveExpirationDate]);

  const [hasCountdownCompleted, setHasCountdownCompleted] = useState(false);

  useEffect(() => {
    setHasCountdownCompleted(false);
  }, [effectiveExpirationDate, isAuthorMode]);

  const dcaSyncStatus = useMemo(() => {
    return getDcaSyncStatus(syncWithDcaMbox, isAuthorMode, dcaPageModel);
  }, [syncWithDcaMbox, dcaPageModel, isAuthorMode]);

  const isWaitingOnTarget = !!(targetMbox && isLoading && !targetData);

  const shouldShowLoadingState = useMemo(() => {
    if (isAuthorMode || dcaSyncStatus !== 'success' || hasCountdownCompleted) {
      return false;
    }

    return isWaitingOnTarget;
  }, [dcaSyncStatus, hasCountdownCompleted, isAuthorMode, isWaitingOnTarget]);

  // Use target data if available, otherwise fall back to props
  const componentData = targetData || rest;

  // ALWAYS show component in author mode to allow configuration
  // In publish mode, apply all visibility rules
  if (!isAuthorMode) {
    // If a sync mbox is authored, the countdown should stay hidden until it resolves successfully.
    if (dcaSyncStatus !== 'success') {
      return null;
    }

    if (!shouldShowLoadingState && !shouldDisplayWithoutTarget) {
      // If no targetMbox is configured, hide immediately (no Target to fetch)
      if (!targetMbox) {
        return null;
      }

      // After loading completes, if no Target data, hide component completely
      if (!isLoading && !targetData) {
        return null;
      }
    }

    /**
     * Hide component when timer is expired unless Target overrides with valid data
     *
     * Priority logic:
     * 1. If Target has valid (non-expired) timer → SHOW (Target overrides AEM)
     * 2. If Target has expired timer → HIDE
     * 3. If no Target data but AEM expired → HIDE
     * 4. If no Target data but AEM valid → SHOW
     *
     * This ensures:
     * - Expired AEM timers hide by default
     * - Target can override ONLY with fresh content
     * - Both expired = hidden
     */
    if (!shouldShowLoadingState && (hasCountdownCompleted || effectiveTimerExpired)) {
      return null;
    }
  }

  // Extract labels from mTimer for countdown mode
  const mTimer = componentData['mTimer'] as Record<string, unknown> | undefined;
  const aBody = componentData['aBody'] as Record<string, unknown> | undefined;
  const mButton = componentData['mButton'] as Record<string, unknown> | undefined;

  // Pass props to presentation component
  const uiKitProps: OCountdownEyebrowBannerPresentationProps = {
    componentId: (componentId as string) || 'countdown-banner',
    authorMode: isAuthorMode,
    isLoading: shouldShowLoadingState,
    expirationDate: (componentData['expirationDate'] as string) || undefined,
    mTimer: mTimer as OCountdownEyebrowBannerTimer | undefined,
    aBody: aBody as OCountdownEyebrowBannerABody | undefined,
    mButton: mButton as OCountdownEyebrowBannerButton | undefined,
    trackingProperties: componentData['trackingProperties'] as Record<string, unknown> | undefined,
    onCountdownComplete: () => {
      if (!isAuthorMode) {
        setHasCountdownCompleted(true);
      }
    },
  };

  return (
    <EditableComponent
      config={OCountdownEyebrowBannerConfig}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(props as any)}
    >
      <div
        data-testid="countdown-eyebrow-banner-comp"
        data-component-name="countdown-eyebrow-banner"
        id={componentId as string}
        className={clsx(styleclass as string, 'fullbleed m-container-fullbleed')}
      >
        <OCountdownEyebrowBannerComp key={`${componentId}-${effectiveExpirationDate}`} {...uiKitProps} />
      </div>
    </EditableComponent>
  );
};
